import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import type { CustomAppConfig } from '@streamlain/view-config';
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import * as styles from '../styles.css';

const configSchema = z.object({
  eyebrow: z.string().default(''),
  title: z.string().default(''),
  subtitle: z.string().default(''),
});

function CustomAppHeaderRenderer({
  config,
  runtimeContext,
}: ViewComponentRenderProps) {
  const typedConfig = config as Partial<z.infer<typeof configSchema>>;
  const customAppConfig = readCustomAppConfig(runtimeContext);
  const customAppDisplayName = readCustomAppDisplayName(runtimeContext);
  const pageTitle = readPageTitle(runtimeContext, customAppConfig);
  const viewTitle =
    typeof runtimeContext?.view?.['title'] === 'string'
      ? runtimeContext.view['title'].trim()
      : '';
  const viewDescription =
    typeof runtimeContext?.view?.['description'] === 'string'
      ? runtimeContext.view['description'].trim()
      : '';
  const title =
    readString(typedConfig.title).trim() ||
    pageTitle ||
    viewTitle ||
    customAppDisplayName;
  const eyebrow =
    readString(typedConfig.eyebrow).trim() || customAppDisplayName;
  const subtitle = readString(typedConfig.subtitle).trim() || viewDescription;

  return createElement(
    'section',
    {
      className: styles.customAppHeader,
      'data-sl-role': 'custom-app-header',
    },
    eyebrow && eyebrow !== title
      ? createElement(
          'div',
          {
            className: styles.customAppHeaderEyebrow,
            'data-sl-field': 'eyebrow',
          },
          eyebrow
        )
      : null,
    createElement(
      'h1',
      { className: styles.customAppHeaderTitle, 'data-sl-field': 'title' },
      title
    ),
    subtitle
      ? createElement(
          'p',
          {
            className: styles.customAppHeaderSubtitle,
            'data-sl-field': 'subtitle',
          },
          subtitle
        )
      : null
  );
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readCustomAppConfig(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): CustomAppConfig | null {
  const customApp = runtimeContext?.customApp;
  const raw =
    customApp && typeof customApp === 'object' && 'config' in customApp
      ? (customApp as { config?: unknown }).config
      : null;
  return raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as CustomAppConfig).pages) &&
    Array.isArray((raw as CustomAppConfig).navigation)
    ? (raw as CustomAppConfig)
    : null;
}

function readCustomAppDisplayName(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): string {
  const customApp = runtimeContext?.customApp;
  const raw =
    customApp && typeof customApp === 'object' && 'branding' in customApp
      ? (customApp as { branding?: unknown }).branding
      : null;
  const record =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : null;
  const settings =
    record?.['settings'] &&
    typeof record['settings'] === 'object' &&
    !Array.isArray(record['settings'])
      ? (record['settings'] as Record<string, unknown>)
      : null;
  const customAppDisplayName =
    typeof settings?.['displayName'] === 'string'
      ? settings['displayName'].trim()
      : '';
  return customAppDisplayName || BRAND_DISPLAY_NAME;
}

function readPageTitle(
  runtimeContext: ViewComponentRenderProps['runtimeContext'],
  customAppConfig: CustomAppConfig | null
): string {
  const page = runtimeContext?.page;
  const titleFormula =
    page &&
    typeof page === 'object' &&
    'titleFormula' in page &&
    page.titleFormula &&
    typeof page.titleFormula === 'object'
      ? (page.titleFormula as Record<string, unknown>)
      : null;
  if (
    titleFormula?.['type'] === 'value' &&
    typeof titleFormula['value'] === 'string'
  ) {
    return titleFormula['value'].trim();
  }

  const pageId =
    page && typeof page === 'object' && typeof page['id'] === 'string'
      ? page['id']
      : null;
  if (!pageId || !customAppConfig) return '';
  const navItem = customAppConfig.navigation.find(
    item => item.pageId === pageId
  );
  const navLabel = navItem?.labelFormula;
  return navLabel?.type === 'value' ? String(navLabel.value).trim() : '';
}

export const CustomAppHeaderComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.CUSTOM_APP_HEADER,
  type: 'navigation',
  name: 'Custom App Header',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    'H'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    eyebrow: '',
    title: '',
    subtitle: '',
  },
  render: CustomAppHeaderRenderer,
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      'H Header'
    );
  },
};
